import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import isEqual from 'lodash/isEqual';
import { debounce } from 'lodash-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import BrandDisplay from '/@/components/brand-display';
import FieldInfo from '/@/components/field-info';
import Icon from '/@/components/icon';
import ModuleLayout from '/@/components/module-layout';
import RecommendationShare from '/@/components/recommendation-share';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { useWeedControlPlansFromAPI, useWeedSystemsFromAPI, useWeedsFromAPI } from '/@/hooks/weeds';
import { _ } from '/@/locale';
import { setSelectedFeature } from '/@/store/nav/actions';
import { getWeedSystemChoicesOptions } from '/@/store/private-data/recommendation-weeds/selectors';
import {
    getRecommendationByIdFromAPI,
    updateRecommendationFromAPI,
} from '/@/store/private-data/recommendations/apiProviders';
import { getAppInstance, getSessionBrands, getSessionCountry } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { isMobile } from '/@/utils';
import { reverse } from '/@/utils/router';
import Select from '/@/v3/components/forms/select';
import Title from '/@/v3/components/title';
import WeedControl from '/@/views/recommendations/recommendation-detail/modules/weed-control';
import styles from './styles.module.css';

const WEED_CONFIG = {
    id: 1,
    weed: 0,
    weedSystem: 0,
    controlPlan: 0,
    showData: false,
    crop: 'crop_protection',
};
@connect(
    (state, props) => {
        const field = props.field;
        const language = state.session.language;
        const brands = getSessionBrands(state);
        const weedZone = field.node.zones.find((zone) => zone.type === 'weeds');
        const allControlPlans = props.controlPlansQuery.data.results;
        const controlPlansFilter = allControlPlans.filter((wcp) => wcp.zone === weedZone.id);
        const allWeedSystems = props.weedSystemsQuery.data.results;
        const weedSystemChoices = getWeedSystemChoicesOptions(controlPlansFilter, language, allWeedSystems);
        const allWeeds = props.weedsQuery.data;
        const appInstance = getAppInstance(state);
        return {
            appInstance,
            language,
            brands,
            field,
            weedZone,
            controlPlansFilter,
            weedSystemChoices,
            allWeedSystems,
            allWeeds,
        };
    },
    (dispatch) =>
        bindActionCreators(
            {
                setSelectedFeature,
                trackEvent,
            },
            dispatch
        )
)
@autobind
class RecommendationWeeds extends Component {
    state = {
        controlPlans: {},
        weedsChoices: {},
        weeds: [WEED_CONFIG],
        controlPlansSelected: [],
        recommendation: {},
    };

    static contextTypes = {
        history: PropTypes.object,
    };

    async fetchData() {
        const reco = await getRecommendationByIdFromAPI({ recoId: this.props.id });
        this.setState({ recommendation: reco, weeds: reco.weeds.sort((a, b) => a.id - b.id) });
        const controlPlansId = reco.weeds.map((weed) => weed.controlPlan);
        reco.weeds.forEach((weedConfig) => {
            this.setState((prevState) => {
                const weedsChoices = { ...prevState.weedsChoices };
                weedsChoices[weedConfig.id] = this.updateWeedChoices(weedConfig, weedConfig.weedSystem, controlPlansId);
                weedConfig.showData = true;
                const system = weedConfig.weedSystem;
                const { controlPlansFilter } = this.props;
                const controlPlan = controlPlansFilter.find((cpf) => {
                    return cpf.systems.includes(system) && cpf.weeds.includes(weedConfig.weed);
                });
                const controlPlans = { ...prevState.controlPlans };
                controlPlans[weedConfig.id] = controlPlan;
                return {
                    weedsChoices,
                    controlPlans,
                };
            });
        });
    }

    componentDidMount() {
        this.fetchData();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.weeds !== this.state.weeds) {
            if (Array.isArray(this.state.weeds)) {
                const isNewWeedCreated = this.state.weeds.find((weed) => {
                    if (weed.weed === 0) {
                        return true;
                    }
                    return false;
                });
                if (!isNewWeedCreated) {
                    this.updateRecommendation();
                }
            }
        }
    }

    updateWeedConfig(weedConfigId, key, value) {
        this.setState((prevState) => {
            let weeds = [...prevState.weeds];

            const weed = { ...weeds.find((el) => el.id === weedConfigId) };
            weed[key] = value;

            weeds = weeds.filter((el) => el.id !== weedConfigId);
            weeds.push(weed);
            weeds.sort((a, b) => a.id - b.id);
            return {
                ...prevState,
                weeds,
            };
        });
    }

    updateWeedChoices(weedConfig, weedSystem, controlPlansSelected) {
        const { controlPlansFilter } = this.props;

        return controlPlansFilter
            .filter((cpf) => {
                return (
                    cpf.systems.includes(weedSystem) &&
                    !(controlPlansSelected.includes(cpf.id) && cpf.id != weedConfig.controlPlan)
                );
            })
            .reduce((weeds, cpf) => {
                const value = cpf.weeds.filter((w) => !weeds.includes(w));
                weeds = weeds.concat(value);
                return weeds;
            }, []);
    }

    @debounce(1000)
    async updateRecommendation() {
        if (this.state.recommendation.id) {
            const recommendationContent = {
                id: this.state.recommendation.id,
                status: 'saved',
                crops: [],
                field: this.props.field.id,
                appInstance: this.props.appInstance.id,
                weeds: this.state.weeds.map((weed) => {
                    return {
                        weed: weed.weed,
                        weedSystem: weed.weedSystem,
                        controlPlan: weed.controlPlan ? weed.controlPlan : null,
                        crop: this.state.recommendation.weeds?.[0]?.crop,
                        plantingDate: this.state.recommendation.weeds?.[0]?.plantingDate,
                        defaultPlantingDate: this.state.recommendation.weeds?.[0]?.defaultPlantingDate,
                    };
                }),
            };
            await updateRecommendationFromAPI(recommendationContent);

            this.setState((oldState) => {
                if (isEqual(oldState?.recommendation, recommendationContent)) {
                    return {
                        ...oldState,
                    };
                }
                return {
                    ...oldState,
                    recommendation: recommendationContent,
                };
            });
        }
    }

    handleChangeSystem(weedSystem, weedConfig) {
        this.setState((prevState) => {
            const weedsChoices = { ...prevState.weedsChoices };
            weedsChoices[weedConfig.id] = this.updateWeedChoices(
                weedConfig,
                weedSystem,
                prevState.controlPlansSelected
            );

            return {
                ...prevState,
                weedsChoices,
            };
        });

        this.updateWeedConfig(weedConfig.id, 'weedSystem', weedSystem);
        this.updateWeedConfig(weedConfig.id, 'weed', null);
        this.updateWeedConfig(weedConfig.id, 'showData', false);

        return weedConfig.weedSystem;
    }

    handleChangeWeed(weed, weedConfig) {
        const { controlPlansFilter } = this.props;

        const system = weedConfig.weedSystem;
        const controlPlan = controlPlansFilter.find((cpf) => {
            return cpf.systems.includes(system) && cpf.weeds.includes(weed);
        });

        this.setState((prevState) => {
            const controlPlans = { ...prevState.controlPlans };
            controlPlans[weedConfig.id] = controlPlan;
            return {
                ...prevState,
                controlPlans,
                controlPlansSelected: Object.values(controlPlans).map((cp) => cp.id),
            };
        });

        this.updateWeedConfig(weedConfig.id, 'weed', weed);
        this.updateWeedConfig(weedConfig.id, 'controlPlan', controlPlan.id);
        this.updateWeedConfig(weedConfig.id, 'showData', true);

        if (weedConfig.controlPlan === 0 && weedConfig.weed === null)
            this.refWeedCombination.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }

    handleAddOther() {
        const { weeds } = this.state;

        const newWeedConfig = { ...WEED_CONFIG };
        newWeedConfig.id = weeds[weeds.length - 1].id + 1;

        this.setState((prevState) => {
            const prevWeeds = prevState.weeds;
            return {
                ...prevState,
                weeds: [...prevWeeds, newWeedConfig],
            };
        });

        this.refWeedCombination.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }

    handleRemove(weedConfig) {
        this.setState((prevState) => {
            const weeds = prevState.weeds.filter((weed) => weed.id !== weedConfig.id);

            const controlPlans = { ...prevState.controlPlans };
            delete controlPlans[weedConfig.id];

            const controlPlansSelected = Object.values(controlPlans).map((cp) => cp.id);

            const weedsChoices = { ...prevState.weedsChoices };
            weeds.forEach((wc) => {
                weedsChoices[wc.id] = this.updateWeedChoices(wc, wc.weedSystem, controlPlansSelected);
            });

            //this.props.removeWeedConfig({ recommendationId: this.props.recommendation.id, weedConfig });

            return {
                ...prevState,
                weeds,
                controlPlans,
                weedsChoices,
                controlPlansSelected,
            };
        });
    }

    getWeedSystemValue(weedConfig) {
        const { weedSystemChoices } = this.props;

        if (weedConfig.weedSystem === 0) return undefined;

        const weedsSystemChoiceValues = weedSystemChoices.map(({ value }) => value);

        if (weedConfig.weedSystem && weedsSystemChoiceValues.includes(weedConfig.weedSystem))
            return weedConfig.weedSystem;

        if (weedSystemChoices && weedSystemChoices.length !== 0) {
            return undefined;
        }

        return weedConfig.selectedWeedSystem;
    }

    getWeedValue(weedConfig) {
        if (weedConfig.weed === 0) return undefined;

        return weedConfig.weed;
    }

    renderModuleCombination(weedConfig) {
        const { weedSystemChoices, language, allWeeds } = this.props;
        const { weeds } = this.state;

        let weedsChoices = [];
        if (this.state.weedsChoices[weedConfig.id]) {
            weedsChoices = this.state.weedsChoices[weedConfig.id].map((weedChoiceId) => {
                const weedChoice = allWeeds.find((weed) => weed.id === weedChoiceId);
                return {
                    value: weedChoiceId,
                    label: weedChoice.name[language],
                };
            });
        }
        const weedSystemValue = this.getWeedSystemValue(weedConfig);
        const weedValue = this.getWeedValue(weedConfig);

        const weedSystemChoicesFiltered = weedSystemChoices.filter((ws) => {
            const selectedChoice = ws.value == weedConfig.weedSystem;
            if (selectedChoice) return true;

            const systemWithoutAvailableWeeds = Object.values(this.state.controlPlans).some((cp) => {
                if (!cp) return false;
                const sameSystem = cp.systems[0] == ws.value;

                if (!sameSystem) {
                    return false;
                }

                const weedConfig = this.state.weeds.find((wc) => wc.controlPlan === cp.id);
                return this.state.weedsChoices[weedConfig.id] && this.state.weedsChoices[weedConfig.id].length <= 1;
            });
            return !systemWithoutAvailableWeeds;
        });

        return (
            <div key={weedConfig.id} className={styles.divider}>
                <div className={styles.containerCombination}>
                    <div className={styles.containerSubtitle}>
                        <Title className={styles.titleCombinations}>{_('Seleccione las combinaciones posibles')}</Title>
                    </div>
                    <div className={styles.containerSelection}>
                        <div className={styles.titleSelect}>
                            <Title className={styles.subtitle}>{_('Seleccione un sistema agrícola')}</Title>
                            <Select
                                value={weedSystemValue}
                                defaultValue={weedSystemValue}
                                size='md'
                                options={weedSystemChoicesFiltered}
                                color={settings.app.theme}
                                appearance='outline'
                                placeholder={_('Sistema agrícola')}
                                onChange={(weedSystem) => this.handleChangeSystem(weedSystem, weedConfig)}
                                className={styles.selectWidth}
                            />
                        </div>
                        <div className={classNames(styles.titleSelect, styles.spaceTitleSelect)}>
                            <Title className={styles.subtitle}>{_('Seleccione una maleza')}</Title>
                            <Select
                                value={weedValue}
                                defaultValue={weedValue}
                                size='md'
                                options={weedsChoices}
                                color={settings.app.theme}
                                appearance='outline'
                                placeholder={_('Maleza')}
                                onChange={(weed) => this.handleChangeWeed(weed, weedConfig)}
                                className={classNames(styles.selectWidth, !weedSystemValue && styles.disabledSelect)}
                                disabled={!weedSystemValue}
                                addNoWrap={false}
                                ellipsis
                            />
                        </div>
                        {weeds.length !== 1 && weedConfig.id !== 1 && (
                            <div className={styles.buttonAdd}>
                                <IconButton color='error' onClick={() => this.handleRemove(weedConfig)}>
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        )}
                    </div>
                </div>
                {weedConfig.showData && this.renderModuleData(weedConfig)}
            </div>
        );
    }

    renderModuleData(weedConfig) {
        const { allWeedSystems } = this.props;
        const weedControlPlan = this.state.controlPlans[weedConfig.id];
        const system = allWeedSystems.filter((weedSystem) => weedSystem.id === weedConfig.weedSystem);
        if (!weedControlPlan) return null;

        return (
            <Fragment>
                <WeedControl
                    system={system}
                    weedControlPlan={weedControlPlan}
                    classNameWeedControl={styles.weedControl}
                />
            </Fragment>
        );
    }

    handleClickMyRecos({ view, selectedFeature }) {
        const data = { view, selectedFeature };
        this.props.setSelectedFeature(data);
        this.context.history(reverse(view));
        const trackingKey = `${view}.${selectedFeature}`;
        this.props.trackEvent({ key: trackingKey.toLowerCase() });
    }

    renderModuleWeeds() {
        const weedsConfigList = this.state.weeds;
        // Si el ultimo weed cargado tiene todos los datos muestra el boton de agregar otro
        const lastWeed = this.state.weeds[this.state.weeds.length - 1];
        let showButtonAdd = true;
        if (!lastWeed?.controlPlan || !lastWeed?.weedSystem || !lastWeed?.weed) {
            showButtonAdd = false;
        }

        return (
            <div className={styles.root}>
                {!isMobile() && (
                    <div className={styles.actions}>
                        <Grid container justifyContent='flex-end' alignItems='center' spacing={1}>
                            <Grid item xs={12} sm={4} lg={2}>
                                <RecommendationShare
                                    type='weed'
                                    recommendation={{
                                        ...this.state.recommendation,
                                        weeds: (this.state.recommendation.weeds || []).map((weed) => ({
                                            ...weed,
                                            controlPlan: this.props.controlPlansFilter.find(
                                                (cpf) => cpf.id === weed?.controlPlan
                                            ),
                                            weed: this.props.allWeeds.find((w) => w.id === weed?.weed),
                                            weedSystem: this.props.allWeedSystems.find(
                                                (w) => w.id === weed?.weedSystem
                                            ),
                                        })),
                                    }}
                                    classNameRoot={styles.buttonRecommendation}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} lg={2}>
                                <Button
                                    variant='outlined'
                                    sx={{ whiteSpace: 'nowrap' }}
                                    fullWidth
                                    onClick={() => this.handleAddOther()}
                                    disabled={!showButtonAdd}
                                >
                                    {_('Agregar otra')}
                                </Button>
                            </Grid>
                        </Grid>
                    </div>
                )}
                <div className={styles.container}>
                    <div className={styles.containerTitle}>
                        <Icon theme='weeds-pests' size='sm' type='weeds' container='circle' padding />
                        <Title className={styles.title}>{_('Manejo de Malezas')}</Title>
                    </div>
                    {weedsConfigList.map((weedConfig) => this.renderModuleCombination(weedConfig))}
                </div>
                <div
                    id={this.refWeedCombination}
                    style={{ float: 'left', clear: 'both' }}
                    ref={(el) => {
                        this.refWeedCombination = el;
                    }}
                />
            </div>
        );
    }

    render() {
        const { field, brands } = this.props;
        const brand = brands.weeds[0]; // TODO: BRAND
        const lastWeed = this.state.weeds[this.state.weeds.length - 1];
        let showButtonAdd = true;
        if (!lastWeed?.controlPlan || !lastWeed?.weedSystem || !lastWeed?.weed) {
            showButtonAdd = false;
        }

        return (
            <ModuleLayout
                sidebar={
                    <Fragment>
                        <BrandDisplay brand={brand} borderRadius={false} />
                        <FieldInfo shouldCollapse={isMobile()} field={field} hideOverlays />
                    </Fragment>
                }
                weeds
                module={this.renderModuleWeeds()}
                className={styles.layoutMainInner}
                classNameActions={styles.actionsStyle}
                actions={
                    isMobile() && (
                        <Grid container justifyContent='flex-end' alignItems='center' spacing={1}>
                            <Grid item xs={12} sm={4} lg={2}>
                                <RecommendationShare
                                    type='weed'
                                    recommendation={this.state.recommendation}
                                    classNameRoot={styles.buttonRecommendation}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} lg={2}>
                                <Button
                                    variant='outlined'
                                    onClick={() => this.handleAddOther()}
                                    disabled={!showButtonAdd}
                                >
                                    {_('Agregar otra')}
                                </Button>
                            </Grid>
                        </Grid>
                    )
                }
            />
        );
    }
}

const RecommendationWeedsWrapper = (props: unknown) => {
    const country = useSelector((state) => getSessionCountry(state));
    const fieldsQuery = useFieldById({
        id: props.field,
    });
    const controlPlansQuery = useWeedControlPlansFromAPI({ page: 1, pageSize: 1000 });
    const weedSystemsQuery = useWeedSystemsFromAPI({ page: 1, pageSize: 10000 });
    const weedsQuery = useWeedsFromAPI({ countryCode: country.code });

    if (
        fieldsQuery.isValidating ||
        weedSystemsQuery.isValidating ||
        controlPlansQuery.isValidating ||
        weedsQuery.isValidating
    )
        return (
            <Box display={'flex'} width={1} height={1} justifyContent={'center'} alignItems={'center'}>
                <Spinner />
            </Box>
        );
    return (
        <RecommendationWeeds
            {...props}
            field={fieldsQuery.data}
            weedSystemsQuery={weedSystemsQuery}
            controlPlansQuery={controlPlansQuery}
            weedsQuery={weedsQuery}
        />
    );
};

export default RecommendationWeedsWrapper;
